import opentype from 'opentype.js'


export type CharBox = {
  width: number;
  left: number;
  height?: number;
  kernedWidth?: number;
  deltaY?: number;
};

export interface TextMetrics {
  /** text width including trailing whitespace */
  width: number;
  /** full line height (ascender → descender, scaled) */
  height: number;
  /** left-most glyph edge (scaled) */
  offsetX: number;
  /** lowest glyph edge (scaled) */
  offsetY: number;

  baselineOffset: number;

  descents: number;

  y1: number;
  
  y2: number;
}


export interface FitOptions {
  lineSpacingRatio?: number;   // px added *between* lines (default 0)
  charSpacing?: number;   // px added *between* glyphs (default 0)
  /** Stop when successive guesses differ by ≤ this many px.  Default 0.5. */
  tolerancePx?: number;
  /** Safety valve: bail out after this many iterations. Default 20. */
  maxIterations?: number;
  startFontSize?: number;
  numberOfLines?: number;
  arcAngle?: number;
}


// Declare Module as a global variable BEFORE loading the script
(window as any).Module = {
  onRuntimeInitialized: () => { }, // we override later
};
interface Wawoff2Module {
  decompress: (data: Uint8Array) => Uint8Array;
  onRuntimeInitialized?: () => void;
}

declare global {
  interface Window {
    Module: Wawoff2Module;
  }

  var Module: Wawoff2Module;
}

export function getGlypthOptions(charSpacing: number) {
  let glyphOpts: opentype.GlyphRenderOptions = {
    kerning: false,
    features: [
      { script: 'arab', tags: ['init', 'medi', 'fina', 'rlig'] },
      { script: 'latn', tags: ['liga', 'rlig'] },
      { script: 'thai', tags: ['liga', 'rlig', 'ccmp'] },
    ],
    hinting: false,
    usePalette: 0,
    drawLayers: true,
    drawSVG: true,
  };

  if (charSpacing) {
    glyphOpts.letterSpacing = charSpacing;
  }

  return glyphOpts
}

export function measureLineInfoForFont(
  font: opentype.Font,
  fontSize: number,
): { asscent: number; descent: number; lineHeight: number; lineGap: number } {
  const upem = font.unitsPerEm || 1000;
  const scale = fontSize / upem;

  const os2 = font.tables?.os2 as any | undefined;
  const hhea = font.tables?.hhea as any | undefined;

  const typoAvailable =
    os2 &&
    typeof os2.sTypoAscender === "number" &&
    typeof os2.sTypoDescender === "number";

  const useTypoFlag =
    os2 && typeof os2.fsSelection === "number" && (os2.fsSelection & 0x80) !== 0;

  const typoAsc = typoAvailable ? os2.sTypoAscender : undefined;
  const typoDesc = typoAvailable ? os2.sTypoDescender : undefined;
  const typoGap = typoAvailable && typeof os2.sTypoLineGap === "number" ? os2.sTypoLineGap : 0;

  const hheaAsc = typeof hhea?.ascender === "number" ? hhea.ascender : undefined;
  const hheaDesc = typeof hhea?.descender === "number" ? hhea.descender : undefined;
  const hheaGap = typeof hhea?.lineGap === "number" ? hhea.lineGap : 0;

  const winAsc = os2 && typeof os2.usWinAscent === "number" ? os2.usWinAscent : undefined;
  const winDesc = os2 && typeof os2.usWinDescent === "number" ? -os2.usWinDescent : undefined;

  const sane = (asc?: number, desc?: number) => {
    if (typeof asc !== "number" || typeof desc !== "number") return false;
    const height = asc - desc;
    return asc > 0 && desc < 0 && height > upem * 0.5 && height < upem * 3;
  };

  // 1) Preferred: OS/2 typo metrics if the font explicitly requests it
  let ascUnits: number | undefined;
  let descUnits: number | undefined;
  let gapUnits: number = 0;

  if (useTypoFlag && sane(typoAsc, typoDesc)) {
    ascUnits = typoAsc!;
    descUnits = typoDesc!;
    gapUnits = typoGap;
  }
  // 2) Otherwise: hhea if sane
  else if (sane(hheaAsc, hheaDesc)) {
    ascUnits = hheaAsc!;
    descUnits = hheaDesc!;
    gapUnits = hheaGap;
  }
  // 3) Otherwise: win metrics (useful “no-clip” fallback)
  else if (sane(winAsc, winDesc)) {
    ascUnits = winAsc!;
    descUnits = winDesc!;
    gapUnits = 0;
  }
  // 4) Last resort: opentype.js convenience values
  else {
    ascUnits = font.ascender ?? 0;
    descUnits = font.descender ?? 0;
    gapUnits = 0;
  }

  // Baseline-to-baseline default is usually asc - desc + gap however, iOS seems to ignore gap in many cases
  // since we want to be compatible with iOS we calculate line height without gap
  let lineHeightUnits = ascUnits - descUnits;
  return {
    asscent: ascUnits * scale,
    descent: descUnits * scale,
    lineHeight: lineHeightUnits * scale,
    lineGap: gapUnits * scale,
  };
}



export function measureActualLineHeight(
  lines: string[],
  font: opentype.font,
  fontSize: number,
  charSpacing: number,
  actualLineGap: number,
  arcAngle: number = 0
) {
  if (!font) return 1.13;
  if (!lines || lines.length === 0) return 0;

  const { lineGap: fontLineGap } = measureLineInfoForFont(font, fontSize);

  const firstMatrix = measureTextMetrics(lines[0], font, fontSize, charSpacing, arcAngle);

  // keep your existing single-line behavior
  if (lines.length === 1) {
    return -firstMatrix.y1;
  }

  let sum = 0;
  let count = 0;

  // (N-1) baseline-to-baseline heights
  for (let i = 0; i < lines.length - 1; i++) {
    const upper = measureTextMetrics(lines[i], font, fontSize, charSpacing, arcAngle);
    const lower = measureTextMetrics(lines[i + 1], font, fontSize, charSpacing, arcAngle);

    const pairLineHeight =
      Math.abs(lower.y1) + Math.abs(upper.y2) - fontLineGap + actualLineGap;

    if (Number.isFinite(pairLineHeight)) {
      sum += pairLineHeight;
      count++;
    }
  }

  // last line "own height" (no next baseline)
  const last = measureTextMetrics(
    lines[lines.length - 1],
    font,
    fontSize,
    charSpacing,
    arcAngle
  );

  // glyph-box height for the last line: ascent + descent
  const lastLineHeight = Math.abs(last.y1) + Math.abs(last.y2);
  if (Number.isFinite(lastLineHeight)) {
    sum += lastLineHeight;
    count++;
  }

  // average over *all lines* (N values: N-1 gaps + last line height)
  // If something went wrong, fall back to your single-line behavior.
  return count > 0 ? sum / lines.length : -firstMatrix.y1;
}


export function getLineCharBoxes(
  line: string,
  font: opentype.Font,
  fontSize: number,
  charSpacing?: number,
  arcAngle: number = 0,
  defaultDeltaY: number = 0
): CharBox[] {
  const boxes: CharBox[] = [];

  if (!line || !font) return boxes;

  let measure = measureTextMetrics
  let textMeasurement = measure( line, font, fontSize, charSpacing, arcAngle, false);
  const glyphOpts = getGlypthOptions(charSpacing);
  const unitsPerEm = font.unitsPerEm;
  const scale = fontSize / unitsPerEm;

  // We’ll collect origin X for each glyph so we can compute kernedWidth later.
  const glyphOrigins: number[] = [];
  const glyphWidths: number[] = [];
  const glyphHeights: number[] = [];

  const hasTrailingSpace = line.endsWith(' ');

let lastX = font.forEachGlyph(
  line,
  0,          // start x
  0,          // start y (baseline)
  fontSize,
  glyphOpts,
  (glyph, gX, gY) => {
    // This is exactly what Glyph.drawMetrics does, but instead of drawing,
    // we read the same values.

    const x = gX; // glyph origin (black vertical line in drawMetrics)
    const y = gY;

    const xMin = glyph.xMin || 0;
    const yMin = glyph.yMin || 0;
    const xMax = glyph.xMax || 0;
    const yMax = glyph.yMax || 0;
    const advanceWidth = glyph.advanceWidth || 0; // not used directly for boxes here

    // Ink box in scaled units
    const inkLeft  = x + xMin * scale;
    const inkRight = x + xMax * scale;
    const inkTop   = y - yMax * scale;
    const inkBot   = y - yMin * scale;

    const inkWidth  = inkRight - inkLeft;
    const inkHeight = inkBot - inkTop;

    glyphOrigins.push(x);                          // origin of this char
    glyphWidths.push(inkWidth || advanceWidth * scale);
    glyphHeights.push(inkHeight || fontSize);
  }
);

// If the last character is a space, extend the last glyph's width
// so that its right edge reaches lastX (which includes the space advance).
if (hasTrailingSpace && glyphOrigins.length > 0) {
  const lastIndex = glyphOrigins.length - 1;

  const currentRight = glyphOrigins[lastIndex] + glyphWidths[lastIndex];
  const logicalRight = lastX; // pen position after the last glyph, including the space

  const extra = logicalRight - currentRight;
  if (extra > 0) {
    glyphWidths[lastIndex] += extra;
  }
}

  const n = glyphOrigins.length;
  if (n === 0) return boxes;

  // Now build the CharBox array
  for (let i = 0; i < n; i++) {
    const originX = glyphOrigins[i] > Math.abs(textMeasurement.offsetX) ? glyphOrigins[i] - textMeasurement.offsetX : glyphOrigins[i];
    const nextX = i + 1 < n ? glyphOrigins[i + 1] : lastX;
    const kernedWidth = nextX - originX; // distance to start of next glyph

    boxes.push({
      left: originX,             // caret at start of this character
      width: glyphWidths[i],     // visual ink width of this glyph
      height: glyphHeights[i],   // visual height
      kernedWidth,               // where selection for this char ends (start of next)
      deltaY: 0,                 // can be style.deltaY later if needed
    });
  }

  return boxes;
}


export function measureTextMetrics(
  text: string,
  font: any,          // opentype.js Font
  fontSize: number,
  charSpacing?: number,
  arcAngle: number = 0,
  shouldTrimSpaces: boolean = false
): TextMetrics {
  const glyphOpts = getGlypthOptions(charSpacing);
  const scale = fontSize / font.unitsPerEm;
  const lineHeight = (font.ascender - font.descender) * scale;

  const lines = text.split('\n');

  // Global bbox over all lines
  let globalX1 = Infinity;
  let globalY1 = Infinity;
  let globalX2 = -Infinity;
  let globalY2 = -Infinity;

  let maxLineWidth = 0;

  lines.forEach((line, i) => {
    const path = font.getPath(line, 0, 0, fontSize, glyphOpts);
    const box = path.getBoundingBox();

    // Logical width from the baseline origin (x=0). Includes trailing spaces.
    const advanceWidth = font.getAdvanceWidth(line, fontSize, glyphOpts);

    // Ink bounds (may start < 0, may extend past advance)
    const inkX1 = box.x1;
    const inkX2 = box.x2;

    // Use ink left edge, and right edge that covers either ink or advance.
    // IMPORTANT: advanceWidth is measured from origin 0, not from box.x1.
    let lineX1 = inkX1;
    let lineX2 = inkX2;
    if (!shouldTrimSpaces && line.startsWith(' ')) {
      // Include leading whitespace: expand left edge to include origin (0),
      // but keep negative ink if present.
      lineX1 = Math.min(inkX1, 0);
    }

    if (!shouldTrimSpaces && line.endsWith(' ')) {
      // Include trailing whitespace + handle whitespace-only lines:
      lineX2 = Math.max(inkX2, advanceWidth);
    }

    // Width in the same coordinate system as lineX1/lineX2
    const lineWidth = lineX2 - lineX1;
    maxLineWidth = Math.max(maxLineWidth, lineWidth);

    // Vertical offset for this line’s baseline
    const baselineY = i * lineHeight;

    // Line bbox, shifted by its baseline
    const lineY1 = box.y1 + baselineY;
    const lineY2 = box.y2 + baselineY;

    // Global bounds
    globalX1 = Math.min(globalX1, lineX1);
    globalX2 = Math.max(globalX2, lineX2);
    globalY1 = Math.min(globalY1, lineY1);
    globalY2 = Math.max(globalY2, lineY2);

  });

  // Fallback for completely empty text
  if (!isFinite(globalX1)) {
    globalX1 = 0;
    globalX2 = 0;
    globalY1 = 0;
    globalY2 = 0;
  }

  const width = maxLineWidth;
  const height = globalY2 - globalY1;
  let {lineGap, asscent, descent} = measureLineInfoForFont(font, fontSize);
  // Distance from top of line box to baseline:
  const baselineOffset =  asscent //+ lineGap;
  if (arcAngle === 0) {
    return {
      width,
      offsetY: -globalY2,
      offsetX: globalX1,
      height,
      y1: globalY1,
      y2: globalY2,
      baselineOffset,
      descents: descent
    };
  }

  // --- Arc layout branch (uses total chord length = width) ---

  const Rb = width / arcAngle;

  // Font vertical metrics in px
  const a = font.ascender * scale; // above baseline

  const Rout = Rb + baselineOffset + a;

  const archWidth  = 2 * Rout * Math.sin(arcAngle / 2);
  const archHeight = Rout * (1 - Math.cos(arcAngle / 2));

    


  return {
    width: archWidth,
    height: archHeight,
    offsetY: -globalY2,
    offsetX: globalX1,
    y1: globalY1,
    y2: globalY2,
    baselineOffset,
    descents: descent
  };



  // // because getBoundingBox only measure inked area, not advance width including trailing spaces we replace the spaces 
  // // with dots and later we rely on advance width for whitespace only text
  // if (text.endsWith(' ')) {
  //   text = text.replace(/ +$/g, match => '.'.repeat(match.length));
  // }
  
  // let glyphOpts = getGlypthOptions(charSpacing);
  // const path = font.getPath(text, 0, 0, fontSize, glyphOpts);
  // const box = path.getBoundingBox();
  
  // let {lineGap, asscent, descent, lineHeight} = measureLineInfoForFont(font, fontSize);

  // // Distance from top of line box to baseline:
  // const baselineOffset =  lineGap + asscent;

  // // Rely on advance width for whitespace-only text where the bounding box is zero.
  // const advanceWidth = font.getAdvanceWidth(text, fontSize, glyphOpts);
  // let width = box.x2 - box.x1 || advanceWidth;
  
  // if (arcAngle == 0) {
  //   return {
  //     width: width,
  //     offsetY: -box.y2,
  //     offsetX: box.x1,
  //     height: box.y2 - box.y1,
  //     y1: box.y1,
  //     y2: box.y2,
  //     baselineOffset
  //   }
  // }
  // const Rb = width / arcAngle;

  // // 3) Font vertical metrics in px
  // const scale = fontSize / font.unitsPerEm;
  // const a = font.ascender * scale;      // above baseline
  // // const d = -font.descender * scale;  // below baseline (not needed for W/H for θ ≤ π)

  // // 4) Outermost radius of the ink
  // const Rout = Rb + a;

  // // 5) AABB (for θ ≤ π, chord horizontal)
  // const archWidth  = 2 * Rout * Math.sin(arcAngle / 2);
  // const archHeight = Rout * (1 - Math.cos(arcAngle / 2));

  // return {
  //     width: archWidth,
  //     height: archHeight,
  //     offsetY: -box.y2,
  //     offsetX: box.x1,
  //     y1: box.y1,
  //     y2: box.y2,
  //     baselineOffset
  //   }

}


export function getFontSizeToMatchWidth(fontInfo: opentype.Font, targetWidth: number, text: string) {
  let lines = text.split('\n');
  let linesSize = lines.map((l) => {
    let TEST_SIZE = 100;
    const fontSizeInPx = targetWidth * TEST_SIZE / fontInfo.getAdvanceWidth(l, TEST_SIZE);
    return fontSizeInPx
  })

  let maxLineSize = Math.max(...linesSize);
  return maxLineSize;
}

export function getFontSizeToMatchRect(
  font: opentype.Font,
  text: string,
  targetWidth: number,
  targetHeight: number,
  opts: FitOptions = {}
): {
  numOfLines: number,
  lineHeight: number,
  fontSize: number
} {
  const maxFontSizePx = 1000;

  const {
    charSpacing = 0,
    lineSpacingRatio = 1,
    tolerancePx = 0.5,
    maxIterations = 20,
    arcAngle = 0,
    startFontSize = maxFontSizePx
  } = opts;

  let lo = 1, hi = maxFontSizePx;
  let bestLines = 0;                       // remember last good layout
  let rect = {
    width: targetWidth,
    height: targetHeight,
  }
  let iteration = 0;
  let fontSize = startFontSize
  let retLineHeight = 0;
  while (hi - lo > tolerancePx) {
    if (iteration > maxIterations) {
      break;
    }
    let result = calculateTextLayout({ text: text, font, fontSize, lineSpacing: lineSpacingRatio, charSpacing, rect, arcAngle })
    if (result.fits) {
      lo = fontSize;            // size works → try bigger
      bestLines = result.lines;
    } else {
      hi = fontSize;            // too big → shrink
    }
    retLineHeight = result.lineHeight;
    fontSize = (lo + hi) / 2;
    iteration++;
  }
  // return { fontSize: lo, lineCount: bestLines };
  return { numOfLines: bestLines, lineHeight: retLineHeight, fontSize: lo }
}


export function getMobileExpectedLineHeight(
  font: opentype.Font, 
  fontSize: number,
  lineSpacing: number,
  lines: string[],
  charSpacing: number, 
  arcAngle: number,
) {
  const lineHeights = lines.map(l => measureTextMetrics(l, font, fontSize, charSpacing, arcAngle, true).height);
  if (lines.length == 1) {
    return lineHeights[0];
  }  

  // we're assuming the following: 
  // Each line before the last line has got the default font's line spacing adjusted with lineSpacing ratio
  // The last line is fitted exactly as the text we are calculating is trying to reproduce iOS layout that trim's the text's image 
  // so that's why we need to take the concrete last's line's height (the ink). 
  // We are assuming that no letter from previous line will be high enough to leak through the last line. 
  const totalHeight = measureLineInfoForFont(font, fontSize).lineHeight * (lines.length - 1) * lineSpacing + lineHeights[lineHeights.length -1] * lineSpacing
  return totalHeight;
}

/**
 * A unified function to calculate text layout.
 *
 * @param {object} options - The layout options.
 * @param {string|string[]} options.text - The input text or pre-split lines.
 * @param {object} options.font - The font object with a `unitsPerEm` property.
 * @param {number} options.fontSize - The font size in pixels.
 * @param {number} options.lineSpacing - The spacing *between* lines in pixels.
 * @param {number} options.charSpacing - The extra spacing between characters.
 * @param {{width: number, height: number}} options.rect - The bounding rectangle.
 * @param {number} [options.exactLineCount=null] - If set, tries to fit the text
 * into this exact number of lines.
 * @returns {{fits: boolean, lines: number, lineHeight: number}} - An object
 * indicating if the text fits, the number of resulting lines, and the average
 * height of a single line (excluding spacing).
 */
function calculateTextLayout({
  text,
  font,
  fontSize,
  lineSpacing,
  charSpacing,
  arcAngle,
  rect
}): {
  fits: boolean,
  lines: number,
  lineHeight: number
} {
  // ---- 1. Common Setup ----

  if (!text || text.length === 0) {
    return { fits: true, lines: 0, lineHeight: 0 };
  }
  let lines = text.split('\n')
  // ---- 2. Line Breaking (Conditional Strategy) ----
  if (arcAngle == 0) {
    // --- Strategy B: Greedy Word Wrapping ---
    lines = greedyWrap(text, font, charSpacing, fontSize, rect.width, arcAngle);
  }

  // ---- 3. Unified Final Validation and Return ----
  if (lines.length === 0) {
    return { fits: true, lines: 0, lineHeight: 0 };
  }

  // Final check that no single line exceeds the width
  const linesMeasurements = lines.map(l => measureTextMetrics(l, font, fontSize, charSpacing, arcAngle, true));
  const lineWidths = linesMeasurements.map(m => m.width);
  const lineHeights = linesMeasurements.map(m => m.height);
  const totalHeight = getMobileExpectedLineHeight(font, fontSize, lineSpacing, lines, charSpacing, arcAngle);
  
  let glphyExactLineHeight = lineHeights.reduce((sum, h) => sum + h, 0) / lineHeights.length;

  if (Math.max(...lineWidths) > rect.width) {
      return { fits: false, lines: lines.length, lineHeight: glphyExactLineHeight };
  }

  const fits = totalHeight <= rect.height;

  // Return the object in the requested format
  return {
    fits,
    lines: lines.length,
    lineHeight: glphyExactLineHeight
  };
}

// -----------------------------------------------------------------------------
// HELPER FUNCTIONS
// -----------------------------------------------------------------------------

/** Small tolerance (px) so wrap decisions are stable across font/scale changes (avoids rounding-induced line-wrap jump). */
export const LINE_WRAP_WIDTH_TOLERANCE = 1.5;

/**
 * (Helper) Performs greedy word wrapping on text.
 * @private
 */
export function greedyWrap(textInput, font, charSpacing, fontSize, maxWidth, arcAngle) {
  const out = [];
  const inputs = Array.isArray(textInput) ? textInput : [textInput];
  const maxWidthWithTolerance = maxWidth + LINE_WRAP_WIDTH_TOLERANCE;

  const width = (s) =>
    measureTextMetrics(s, font, fontSize, charSpacing, arcAngle, true).width;

  // longest prefix of s that fits maxWidth (binary search)
  const takePrefixThatFits = (s) => {
    let lo = 1, hi = s.length, best = 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (width(s.slice(0, mid)) <= maxWidthWithTolerance) {
        best = mid;
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return best;
  };

  const wrapHardLine = (line) => {
    // preserve explicit empty lines
    if (line === '') {
      out.push('');
      return;
    }

    let cur = '';
    const tokens = line.match(/\s+|[^\s]+/g) ?? [''];

    const flush = () => {
      if (cur !== '') out.push(cur);
      cur = '';
    };

    for (let token of tokens) {
      while (token.length) {
        const candidate = cur + token;

        if (width(candidate) <= maxWidthWithTolerance) {
          cur = candidate;
          break;
        }

        // if we already have something on the line, flush it and retry token on a new line
        if (cur !== '') {
          flush();
          continue;
        }

        // token itself doesn't fit on an empty line -> split token
        const n = takePrefixThatFits(token);
        out.push(token.slice(0, n));
        token = token.slice(n);
      }
    }

    flush();
  };

  for (const raw of inputs) {
    const hardLines = String(raw).split(/\r?\n/); // <-- enforces your newline expectations
    for (const hl of hardLines) wrapHardLine(hl);
  }

  return out;
}

/**
 * thanks - https://chatgpt.com/share/e/686fd1f5-5f48-8009-adbf-586900945c40
 * (Helper) Finds exact line breaks using DFS with memoization.
 * @private
 */
function _findBreaksDFS(tokens, widths, lineCount, maxWidth) {
  const memo = new Map();
  const breaks = [];

  function dfs(idx, linesLeft) {
    const key = `${idx}|${linesLeft}`;
    if (memo.has(key)) return memo.get(key);

    if (linesLeft === 0) return memo.set(key, idx === tokens.length).get(key);
    if (idx >= tokens.length) return memo.set(key, false).get(key);

    let currentLineWidth = 0;
    for (let j = idx; j < tokens.length; ++j) {
      currentLineWidth += widths[j];
      if (currentLineWidth > maxWidth) break;

      if (dfs(j + 1, linesLeft - 1)) {
        breaks.push(j + 1);
        return memo.set(key, true).get(key);
      }
    }
    return memo.set(key, false).get(key);
  }

  if (!dfs(0, lineCount)) return null;

  breaks.push(0);
  breaks.sort((a, b) => a - b);
  return breaks;
}



function releaseWoff2HeapBytes(ttfBytes: Uint8Array | null) {
  const moduleRef = (window as any).Module;
  if (!ttfBytes || !moduleRef?._free || !moduleRef?.HEAPU8?.buffer) {
    return;
  }

  if (ttfBytes.buffer === moduleRef.HEAPU8.buffer) {
    moduleRef._free(ttfBytes.byteOffset);
  }
}

export async function loadFontMetrics(name: string, path: string): Promise<opentype.Font | null> {
  let ttfBytes: Uint8Array | null = null;
  try {
    try {
      const font = await opentype.load(path);
      return font;
    } catch (err) {
      await loadWawoff2();
      const res = await fetch(path, { mode: 'cors' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const buf = await res.arrayBuffer();
      let bytes: Uint8Array | null = new Uint8Array(buf);

      if (!bytes) {
        console.error('couldn\'t convert buf to bytes');
        return null;
      }

      /* optional sanity-check: should log “wOF2” */
      console.log('magic:', new TextDecoder().decode(bytes.slice(0, 4)));

      ttfBytes = Module.decompress(bytes);

      if (!ttfBytes) {
        const font = await opentype.load(path);
        return font;
      }

      // ✨ get a clean ArrayBuffer that starts exactly at the TTF data
      const ttfBuffer = ttfBytes.buffer.slice(
        ttfBytes.byteOffset,
        ttfBytes.byteOffset + ttfBytes.byteLength
      );

      const font = opentype.parse(ttfBuffer);
      console.log("Font loaded:", font.names.fullName);
      return font;
    }
  } catch (err) {
    console.error("Error in loadFontMetrics:", err);
    return null;
  } finally {
    releaseWoff2HeapBytes(ttfBytes);
  }
}

const loadScript = (url): Promise<boolean> => {
  return new Promise((resolve, reject) => {
    // Check if script already exists
    if (document.querySelector(`script[src="${url}"]`)) {
      resolve(true); // Already loaded
      return;
    }

    const script = document.createElement('script');
    script.src = url;
    script.async = true;

    script.onload = () => resolve(true);
    script.onerror = () => reject(new Error(`Failed to load script: ${url}`));

    document.head.appendChild(script);
  });
}

// This variable, living outside the function, will cache the promise.
let wawoff2Promise: Promise<void> | null = null;

/**
 * Loads the WOFF2 decompression module.
 * Ensures the underlying script is fetched and initialized only once,
 * even if called multiple times in parallel.
 */
export function loadWawoff2(): Promise<void> {
  // If the loading process has already started, return the existing promise.
  if (wawoff2Promise) {
    return wawoff2Promise;
  }

  // If this is the first call, create a new promise and cache it.
  wawoff2Promise = new Promise(async (resolve, reject) => {
    // 1. Handle case where the module is already loaded and ready.
    if ((window as any).Module?.decompress) {
      return resolve();
    }

    // 2. Set up the global 'Module' object. The script will attach to this
    //    and call 'onRuntimeInitialized' when it's ready.
    (window as any).Module = {
      onRuntimeInitialized: () => {
        // This is the official signal that the module is ready to be used.
        resolve();
      }
    };

    // 3. Load the script. The promise will be resolved by the callback above, not here.
    try {
      // Assuming you have a 'loadScript' helper function.
      await loadScript("/vendors/wof2-decompile/decompress_binding.js");
    } catch (err) {
      // If the script fails to download, reject the promise and clear the cache.
      wawoff2Promise = null; 
      reject(err);
    }
  });

  return wawoff2Promise;
}
